/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Box,
  Chip,
  useTheme,
  Paper,
  CircularProgress,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/store";
import {
  selectIsOrderLoading,
  selectOrderTracking,
} from "src/slices/orders/selectors";
import { fetchOrderTrackingById } from "src/slices/orders/thunks";

interface IOrderTrackingModalProps {
  open: boolean;
  handleClose: () => void;
  orderId: string | number;
  channel: string | null;
  specialInstructions: string | null;
}

const OrderTrackingModal = ({
  open,
  handleClose,
  orderId,
  channel,
  specialInstructions,
}: IOrderTrackingModalProps) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const isLoading = useSelector(selectIsOrderLoading);
  const trackingData = useSelector(selectOrderTracking);

  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      void dispatch(fetchOrderTrackingById(orderId));
      setShowInstructions(false); // reset when modal opens
    }
  }, [open, orderId]);

  // Delay showing special instructions
  useEffect(() => {
    if (!isLoading && trackingData) {
      const timer = setTimeout(() => setShowInstructions(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, trackingData]);

  const timeline = trackingData?.timeline || [];
  const cancelReason = trackingData?.cancelReason ?? null;
  const createdBy = trackingData?.createdBy ?? null;
  const dueDate = trackingData?.dueDate ? new Date(trackingData.dueDate) : null;
  const isDueDatePassed =
    dueDate && new Date().setHours(0, 0, 0, 0) > dueDate.setHours(0, 0, 0, 0);

  // console.log("DueDate:", trackingData?.dueDate);

  const getStatusColor = (text: string) => {
    const s = text?.toLowerCase() || "";
    if (s.includes("new order")) return theme.palette.orderStatus?.newOrder;
    if (s.includes("cancel") || s.includes("failed"))
      return theme.palette.orderStatus?.cancelled;
    if (s.includes("pickup")) return theme.palette.orderStatus?.readyForPickup;
    if (s.includes("progress")) return theme.palette.orderStatus?.inProgress;
    if (s.includes("delivery"))
      return theme.palette.orderStatus?.readyForDelivery;
    if (s.includes("deliver")) return theme.palette.orderStatus?.delivered;
    return { main: theme.palette.grey[400], contrastText: "#000" };
  };

  const getEnteredBy = () => {
    if (createdBy) {
      return `Given By: ${createdBy?.name ?? "N/A"}${
        createdBy?.roles?.length
          ? ` (${createdBy.roles.join(", ")}${
              createdBy.locations?.length
                ? ` - ${createdBy.locations.join(", ")}`
                : ""
            })`
          : ""
      }`;
    }
    if (!channel) return "";
    if (channel === "PORTAL") return "Given By: CSR";
    if (channel === "MOBILE_APP") return "Given By: End Customer";
    return "";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: 8, overflow: "hidden" },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          px: 3,
          py: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 600, color: "white" }}>
            Order Tracking
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          maxHeight: "75vh",
          overflow: "hidden",
          overflowY: "auto",
          m: 1,
        }}
      >
        {isLoading ? (
          // 🔄 Show loader only
          <Box textAlign="center" my={4}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Loading order tracking...
            </Typography>
          </Box>
        ) : (
          <>
            {isDueDatePassed && (
              <Fade in timeout={500}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  bgcolor="rgba(255, 0, 0, 0.05)" // very light red background
                  color={theme.palette.error.main} // main red text
                  border={`1px solid rgba(255, 0, 0, 0.2)`} // subtle border
                  borderRadius={1.5} // smaller curve
                  p={1.2} // less padding → smaller height
                  mb={1.5} // less margin below
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Due Date Passed
                  </Typography>
                  {trackingData?.dueDate && (
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      (Due:{" "}
                      {new Date(trackingData.dueDate).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }
                      )}
                      )
                    </Typography>
                  )}
                </Box>
              </Fade>
            )}

            {/* 📋 Table */}
            {timeline.length > 0 ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  maxHeight: "65vh",
                  overflowX: "auto",
                  overflowY: "auto",
                  cursor: "grab",
                  "&:active": { cursor: "grabbing" },
                  "&::-webkit-scrollbar": { height: 6, width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.grey[400],
                    borderRadius: 4,
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: theme.palette.grey[500],
                  },
                }}
              >
                <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                      <TableCell sx={{ fontWeight: 600, width: "20%" }}>
                        Action
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "15%" }}>
                        Action By
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "15%" }}>
                        Action Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "25%" }}>
                        Transition
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "25%" }}>
                        Comments
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {timeline.map((row: any, i: number) => {
                      const color = getStatusColor(row.action);
                      const name = row?.actionBy?.name || "N/A";
                      const roles = (row?.actionBy?.roles || [])
                        .map((r: string) => r.trim())
                        .join(", ");
                      const locations = (row?.actionBy?.locations || [])
                        .map((l: string) => l.trim())
                        .join(", ");
                      const finalComment =
                        row.action?.toLowerCase().includes("cancel") &&
                        cancelReason
                          ? cancelReason
                          : row.comment;

                      return (
                        <TableRow key={i} hover>
                          <TableCell>
                            <Chip
                              label={row.action || "-"}
                              sx={{
                                bgcolor: color?.main,
                                color: color?.contrastText,
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                borderRadius: "8px",
                                px: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {name}
                            </Typography>
                            {roles && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                ({roles}
                                {locations ? ` - ${locations}` : ""})
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.updatedAt
                              ? new Date(row.updatedAt).toLocaleString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  }
                                )
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.transition || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              maxWidth: 350,
                            }}
                          >
                            {finalComment ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontStyle: "italic",
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                {finalComment}
                              </Typography>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography textAlign="center" my={3}>
                No tracking history found.
              </Typography>
            )}

            {/* 🗒️ Special Instructions */}
            <Fade in={showInstructions && !!specialInstructions} timeout={500}>
              <Box
                mt={3}
                p={2.5}
                border={`1px solid ${theme.palette.divider}`}
                borderRadius={2}
                bgcolor={theme.palette.grey[50]}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Special Instructions
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    whiteSpace: "pre-line",
                    wordBreak: "break-word",
                  }}
                >
                  {specialInstructions}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontStyle: "italic",
                  }}
                >
                  {getEnteredBy()}
                </Typography>
              </Box>
            </Fade>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderTrackingModal;
